/**
 * Feature Flags Table Component
 */

import { Card, CardContent, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Badge, Switch, Button } from "@wondermaps/ui";
import { Link, useNavigate } from "@tanstack/react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFlag } from "@fortawesome/pro-solid-svg-icons/faFlag";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons/faTrashCan";
import { faPencil } from "@fortawesome/pro-solid-svg-icons/faPencil";
import type { FeatureFlag } from "../../hooks/useSuperadminFeatureFlags";

type Props = {
  flags: FeatureFlag[];
  overrideCounts: Record<string, number> | undefined;
  isToggling: boolean;
  onToggle: (id: string, enabled: boolean) => void;
  onDelete: (flag: FeatureFlag) => void;
};

export function FeatureFlagsTable({ flags, overrideCounts, isToggling, onToggle, onDelete }: Props) {
  const navigate = useNavigate();
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Key</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Overrides</TableHead>
              <TableHead className="w-[100px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {flags.map((flag) => (
              <TableRow key={flag.id} className="cursor-pointer" onClick={() => navigate({ to: "/superadmin/feature-flags/$flagId", params: { flagId: flag.id } })}>
                <TableCell className="font-mono text-sm">
                  <Link to="/superadmin/feature-flags/$flagId" params={{ flagId: flag.id }} className="hover:underline">
                    {flag.key}
                  </Link>
                </TableCell>
                <TableCell className="font-medium">{flag.name}</TableCell>
                <TableCell><Badge variant="outline">{flag.flagType}</Badge></TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Switch checked={flag.enabled} onCheckedChange={(enabled) => onToggle(flag.id, enabled)} disabled={isToggling} />
                </TableCell>
                <TableCell>
                  {flag.requiredTier ? <Badge variant="secondary">{flag.requiredTier}</Badge> : <span className="text-muted-foreground">-</span>}
                </TableCell>
                <TableCell><Badge variant="outline">{flag.category}</Badge></TableCell>
                <TableCell className="text-right">{overrideCounts?.[flag.id] ?? 0}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                      <Link to="/superadmin/feature-flags/$flagId" params={{ flagId: flag.id }}><FontAwesomeIcon icon={faPencil} className="h-4 w-4" /></Link>
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => onDelete(flag)}><FontAwesomeIcon icon={faTrashCan} className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function FeatureFlagsTableSkeleton() {
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Key</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Overrides</TableHead>
              <TableHead className="w-[50px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {[1, 2, 3, 4].map((i) => (
              <TableRow key={i}>
                <TableCell><Skeleton className="h-5 w-28" /></TableCell>
                <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                <TableCell><Skeleton className="h-5 w-10" /></TableCell>
                <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                <TableCell><Skeleton className="h-5 w-8 ml-auto" /></TableCell>
                <TableCell><Skeleton className="h-5 w-8" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function FeatureFlagsEmpty() {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <FontAwesomeIcon icon={faFlag} className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">No feature flags found.</p>
      </CardContent>
    </Card>
  );
}
